// QRadar AQL datasource mappings.
//
// Scope: endpoint:wineventlog + endpoint:sysmon. Log-category-scoped EventIDs
// live in `qradarEventCodeByLogCategory` below — the same pattern Splunk uses —
// so a single category (e.g. process_creation) can emit different EventIDs
// depending on whether the customer's log source is Sysmon (EventCode 1) or
// WinEventLog (EventCode 4688).
//
// `qradarMap.windows` still carries repo + selectFields + default (wineventlog)
// EventIDs so the per-category (non-grouped) render path produces a complete
// query without a logCategory directive.
import type { DatasourceMap } from '../datasource.types'

export const qradarMap: DatasourceMap = {
  'QRadar Query': {
    any: {
      process_creation: { repo: 'events' },
      authentication_events: { repo: 'events' },
      file_event: { repo: 'events' },
      registry_event: { repo: 'events' },
      network_connection: { repo: 'events' },
      dns: { repo: 'events' },
      image_load: { repo: 'events' },
      pipe_created: { repo: 'events' },
      system: { repo: 'events' },
      security: { repo: 'events' },
      Rdp_event: { repo: 'events' },
    },

    windows: {
      process_creation: {
        repo: 'events',
        eventCodes: ['4688'],
        selectFields: [
          'process_path',
          'process_name',
          'process_cmdline',
          'process_id',
          'parent_process_name',
          'parent_process_path',
          'parent_process_cmdline',
          'parent_process_id',
          'hostname',
          'username',
          'event_id',
        ],
      },
      authentication_events: {
        repo: 'events',
        eventCodes: ['4624', '4625', '4648', '4672', '4776'],
        selectFields: ['username', 'hostname', 'src_ip', 'domain', 'event_id'],
      },
      file_event: {
        repo: 'events',
        eventCodes: ['4663'],
        selectFields: [
          'target_path',
          'process_name',
          'process_path',
          'process_cmdline',
          'hostname',
          'username',
          'event_id',
        ],
      },
      registry_event: {
        repo: 'events',
        eventCodes: ['4657'],
        selectFields: [
          'registry_key_name',
          'registry_path',
          'registry_value_name',
          'process_name',
          'hostname',
          'username',
          'event_id',
        ],
      },
      network_connection: {
        repo: 'events',
        eventCodes: ['5156'],
        selectFields: [
          'src_ip',
          'dest_ip',
          'dest_port',
          'protocol',
          'process_name',
          'process_path',
          'hostname',
          'username',
          'event_id',
        ],
      },
      system: {
        repo: 'events',
        eventCodes: ['7045', '7036'],
        selectFields: ['service_name', 'service_file_path', 'hostname', 'event_id'],
      },
      security: {
        repo: 'events',
        eventCodes: ['4698', '4702'],
        selectFields: ['task_category', 'process_name', 'hostname', 'username', 'event_id'],
      },
      dns: {
        repo: 'events',
        eventCodes: ['22'],
        selectFields: [
          'dns_query',
          'dns_answer',
          'hostname',
          'process_name',
          'username',
          'event_id',
        ],
      },
      image_load: {
        repo: 'events',
        eventCodes: ['7'],
        selectFields: [
          'object_loaded',
          'process_name',
          'process_path',
          'hostname',
          'username',
          'event_id',
        ],
      },
      Rdp_event: {
        repo: 'events',
        eventCodes: ['4624', '4778', '4779'],
        selectFields: ['username', 'hostname', 'src_ip', 'event_id'],
      },
      pipe_created: {
        repo: 'events',
        eventCodes: ['17', '18'],
        selectFields: [
          'pipe_name',
          'process_name',
          'process_path',
          'hostname',
          'username',
          'event_id',
        ],
      },
    },

  },
}

/**
 * Per-logCategory EventID overrides. When an AST carries
 * `directives.datasource.logCategory`, wrapQRadarDatasource uses this table
 * (keyed by [logCategory][huntCategory]) to override `ds.eventCodes`.
 *
 * Only entries where a native EventID exists for the (logCategory,
 * huntCategory) pair are populated. A missing entry means this log source
 * doesn't carry that hunt category (e.g. DNS/ImageLoad on wineventlog, or
 * system/security on sysmon) — the renderer falls back to the default
 * eventCodes on the resolved datasource, which may be empty.
 */
export const qradarEventCodeByLogCategory: Record<
  string,
  Record<string, string[]>
> = {
  'endpoint:sysmon': {
    process_creation: ['1'],
    file_event: ['11'],
    registry_event: ['13'],
    image_load: ['7'],
    pipe_created: ['17', '18'],
    dns: ['22'],
    network_connection: ['3'],
  },
  'endpoint:wineventlog': {
    process_creation: ['4688'],
    file_event: ['4663'],
    registry_event: ['4657'],
    system: ['7045', '7036'],
    security: ['4698', '4702', '4699'],
    authentication_events: ['4624', '4625', '4648', '4672', '4776'],
    Rdp_event: ['4624', '4778', '4779'],
    network_connection: ['5156'],
  },
}
